import apiClient, { API_ENDPOINTS } from 'api';
import {
  extractTemplatePayload,
  getPublishedActiveTemplate,
  setActiveTemplateApiState
} from '../../template/TemplateStorage.jsx';

const { templates } = API_ENDPOINTS;
const activeTemplateRequests = new Map();
const activeTemplateResultsThisLoad = new Map();
const activeTemplateCacheKey = (siteKey, companyId, domainType) => (
  `academy:active-template:v3:${siteKey || 'current'}:${companyId || ''}:${domainType || ''}`
);
const publicTemplateRequestOptions = {
  credentials: 'same-origin',
  skipAuth: true
};

const rememberActiveTemplateCompany = (companyId) => {
  try {
    if (companyId) {
      localStorage.setItem('CompPrimaryId', String(companyId));
    }
  } catch {
  }
};

const parseJsonPayload = (payload) => {
  if (typeof payload !== 'string') return payload;
  const trimmed = payload.trim();
  if (!trimmed) return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    return payload;
  }
};

const TEMPLATE_RESPONSE_KEYS = ['data', 'payload', 'result', 'template', 'uiTemplate'];

const isTemplateLike = (value) => (
  value
  && typeof value === 'object'
  && !Array.isArray(value)
  && (
    Array.isArray(value.pages)
    || Boolean(value.templateKey)
    || Boolean(value.baseTemplateKey)
    || Boolean(value.attributes?.sourceDomain)
    || Boolean(value.domain?.type)
  )
);

const collectTemplatePayloads = (payload) => {
  const value = parseJsonPayload(payload);
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.flatMap(collectTemplatePayloads);
  }

  if (isTemplateLike(value)) {
    const template = extractTemplatePayload(value);
    return template ? [template] : [];
  }

  if (typeof value !== 'object') return [];
  if (String(value.status || '').toLowerCase() === 'failed') return [];

  return TEMPLATE_RESPONSE_KEYS.flatMap((key) => {
    if (value[key] === undefined || value[key] === value) return [];
    return collectTemplatePayloads(value[key]);
  });
};

const normalizeDomainType = (value) => String(value || '').trim().toUpperCase().replace(/[\s-]+/g, '_');

const templateCompanyIdOf = (template) => (
  template?.company?.primaryId
  || template?.companyId
  || template?.attributes?.companyId
);

const templateDomainTypeOf = (template) => (
  template?.domain?.type
  || template?.attributes?.sourceDomain
  || ''
);

const isSystemDefaultTemplate = (template) => (
  template?.systemDefault === true
  || String(template?.systemDefault).toLowerCase() === 'true'
);

const isPublishedSystemDefaultTemplate = (template, domainType) => {
  if (!isSystemDefaultTemplate(template)) return false;

  const lifecycleStatus = String(template.lifecycleStatus || template.status || 'PUBLISHED').toUpperCase();
  if (lifecycleStatus !== 'PUBLISHED') return false;

  const requestedDomainType = normalizeDomainType(domainType);
  const templateDomainType = normalizeDomainType(templateDomainTypeOf(template));
  return !requestedDomainType || !templateDomainType || templateDomainType === requestedDomainType;
};

const templateIdScoreOf = (template) => {
  const value = Number(template?.primaryId ?? template?.publishedVersionId ?? template?.id ?? template?._id ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const templateVersionScoreOf = (template) => {
  const value = Number(template?.version ?? template?.attributes?.version ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const templateDateScoreOf = (template) => {
  const value = template?.modifiedDate
    || template?.updatedDate
    || template?.updatedAt
    || template?.modifiedOn
    || template?.createdDate
    || template?.createdAt;
  if (!value) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'object' && Number.isFinite(Number(value.time))) return Number(value.time);
  if (typeof value === 'object' && Number.isFinite(Number(value.$numberLong))) return Number(value.$numberLong);
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

const templateFreshnessScoreOf = (template) => (
  templateVersionScoreOf(template) * 1_000_000_000_000
  + templateDateScoreOf(template)
  + templateIdScoreOf(template)
);

const isPublishedDomainTemplate = (template, domainType) => {
  if (!template || isSystemDefaultTemplate(template)) return false;
  const lifecycleStatus = String(template.lifecycleStatus || template.status || 'PUBLISHED').toUpperCase();
  if (lifecycleStatus !== 'PUBLISHED') return false;

  const requestedDomainType = normalizeDomainType(domainType);
  const templateDomainType = normalizeDomainType(templateDomainTypeOf(template));
  return !requestedDomainType || !templateDomainType || templateDomainType === requestedDomainType;
};

const isPublishedCompanyDomainTemplate = (template, companyId, domainType) => (
  isPublishedDomainTemplate(template, domainType)
  && (!templateCompanyIdOf(template)
    || String(templateCompanyIdOf(template)) === String(companyId))
);

const isExplicitEmptyTemplateList = (payload) => {
  const value = parseJsonPayload(payload);
  if (Array.isArray(value)) return value.length === 0;
  if (!value || typeof value !== 'object') return false;

  return ['data', 'payload', 'result', 'templates', 'items'].some((key) => (
    value[key] !== undefined
    && value[key] !== value
    && isExplicitEmptyTemplateList(value[key])
  ));
};

const sortByFreshness = (templatesList) => (
  [...templatesList].sort((a, b) => templateFreshnessScoreOf(b) - templateFreshnessScoreOf(a))
);

const pickLatestCompanyPublishedTemplate = (payload, context = {}) => {
  const candidates = collectTemplatePayloads(payload)
    .filter((template) => isPublishedDomainTemplate(template, context.domainType));
  if (!candidates.length) return null;

  const requestedCompanyId = context.companyId === undefined || context.companyId === null
    ? ''
    : String(context.companyId);
  const exactCompanyCandidates = requestedCompanyId
    ? candidates.filter((template) => String(templateCompanyIdOf(template) || '') === requestedCompanyId)
    : [];

  if (requestedCompanyId) {
    return exactCompanyCandidates.length ? sortByFreshness(exactCompanyCandidates)[0] : null;
  }

  return sortByFreshness(candidates)[0];
};

const latestPublishedTemplateFindCriteria = (companyId, domainType) => ({
  company: { primaryId: companyId },
  domain: { type: domainType },
  lifecycleStatus: 'PUBLISHED',
  systemDefault: false,
  deleted: false,
});

const persistResolvedTemplateContext = (template, fallbackDomainType) => {
  try {
    const templateCompanyId = templateCompanyIdOf(template);
    const templateDomainType = templateDomainTypeOf(template) || fallbackDomainType;
    if (templateCompanyId) localStorage.setItem('CompPrimaryId', String(templateCompanyId));
    if (templateDomainType) localStorage.setItem('domainType', String(templateDomainType));
  } catch {
  }
};

const fetchLatestCompanyPublishedTemplate = async (companyId, domainType) => {
  if (!companyId) {
    return {
      responseCount: 0,
      template: null,
      explicitEmpty: false,
      source: 'none'
    };
  }

  try {
    const activePayload = await apiClient.get(templates.active, {
      ...publicTemplateRequestOptions,
      query: {
        companyId,
        domainType
      }
    });
    const activeTemplate = extractTemplatePayload(activePayload);
    const activeResponseCount = getTemplateApiResponseCount(activePayload);
    // The backend returns a published system template when the company has not
    // selected a company-specific template yet. That is a valid built-in
    // default decision, not an invalid tenant-template response.
    if (isPublishedSystemDefaultTemplate(activeTemplate, domainType)) {
      return {
        responseCount: 0,
        template: null,
        explicitEmpty: true,
        source: 'active'
      };
    }

    if (activeTemplate || activeResponseCount > 0 || isExplicitEmptyTemplateList(activePayload)) {
      return {
        responseCount: activeResponseCount || (activeTemplate ? 1 : 0),
        template: isPublishedCompanyDomainTemplate(activeTemplate, companyId, domainType)
          ? activeTemplate
          : null,
        explicitEmpty: !activeTemplate && isExplicitEmptyTemplateList(activePayload),
        source: 'active'
      };
    }
  } catch {
    // Older deployments may not expose /active; keep the legacy /find fallback below.
  }

  const criteria = latestPublishedTemplateFindCriteria(companyId, domainType);
  const payload = await apiClient.post(templates.find, criteria, publicTemplateRequestOptions);
  return {
    responseCount: getTemplateApiResponseCount(payload),
    template: pickLatestCompanyPublishedTemplate(payload, { companyId, domainType }),
    explicitEmpty: isExplicitEmptyTemplateList(payload),
    source: 'find'
  };
};

export const getTemplateApiResponseCount = (payload) => {
  const value = parseJsonPayload(payload);
  if (value === undefined || value === null || value === '') return 0;
  if (Array.isArray(value)) return value.length;
  if (typeof value !== 'object') return 1;

  for (const key of TEMPLATE_RESPONSE_KEYS) {
    if (value[key] === undefined || value[key] === value) continue;
    return getTemplateApiResponseCount(value[key]);
  }

  return extractTemplatePayload(value) ? 1 : 0;
};

const readCachedActiveTemplate = (siteKey, companyId, domainType) => {
  try {
    const raw = sessionStorage.getItem(activeTemplateCacheKey(siteKey, companyId, domainType));
    return raw ? extractTemplatePayload(raw) : undefined;
  } catch {
    return undefined;
  }
};

const writeCachedActiveTemplate = (siteKey, companyId, domainType, template) => {
  try {
    sessionStorage.setItem(activeTemplateCacheKey(siteKey, companyId, domainType), JSON.stringify(template || null));
  } catch {
    // Ignore storage quota/private mode failures; callers still receive the API result.
  }
};

export const resolveActiveTemplate = async (companyId, domainType, options = {}) => {
  rememberActiveTemplateCompany(companyId);
  const forceRefresh = options?.forceRefresh === true;
  const hasCompanyScope = companyId !== undefined && companyId !== null && String(companyId).trim() !== '';
  const siteKey = options?.siteKey || 'current';

  if (!forceRefresh) {
    const cached = readCachedActiveTemplate(siteKey, companyId, domainType);
    if (cached !== undefined) {
      const publishedCachedTemplate = getPublishedActiveTemplate(cached, { companyId, domainType });
      if (publishedCachedTemplate || cached === null) {
        return {
          status: publishedCachedTemplate ? 'assigned' : 'default',
          template: publishedCachedTemplate,
          responseCount: publishedCachedTemplate ? 1 : 0,
          reason: publishedCachedTemplate ? 'cached-template' : 'cached-empty',
          source: 'cache',
          checkedAt: null,
        };
      }
    }
  }

  if (!hasCompanyScope) {
    return { status: 'error', template: null, responseCount: null, reason: 'missing-company' };
  }

  const cacheKey = activeTemplateCacheKey(siteKey, companyId, domainType);
  if (forceRefresh && activeTemplateResultsThisLoad.has(cacheKey)) {
    return activeTemplateResultsThisLoad.get(cacheKey);
  }

  if (activeTemplateRequests.has(cacheKey)) {
    return activeTemplateRequests.get(cacheKey);
  }

  const request = (async () => {
    const findResult = await fetchLatestCompanyPublishedTemplate(companyId, domainType);
    const publishedTemplate = findResult.template;
    const responseCount = publishedTemplate ? 1 : findResult.responseCount;
    const status = publishedTemplate
      ? 'assigned'
      : findResult.explicitEmpty && responseCount === 0
        ? 'default'
        : 'error';
    const resolution = {
      status,
      template: publishedTemplate || null,
      responseCount,
      reason: status === 'default'
        ? 'empty-template-response'
        : status === 'assigned'
          ? 'assigned-template'
          : (responseCount > 0 ? 'invalid-template-response' : 'template-request-unavailable'),
      source: findResult.source,
      checkedAt: Date.now(),
    };

    if (publishedTemplate) {
      const resolvedCompanyId = templateCompanyIdOf(publishedTemplate) || companyId;
      const resolvedDomainType = templateDomainTypeOf(publishedTemplate) || domainType;
      persistResolvedTemplateContext(publishedTemplate, domainType);
      writeCachedActiveTemplate(siteKey, resolvedCompanyId, resolvedDomainType, publishedTemplate);
    }

    setActiveTemplateApiState(companyId, domainType, {
      status: status === 'error' ? 'error' : 'success',
      responseCount,
      hasPublishedTemplate: Boolean(publishedTemplate),
      resolutionStatus: status,
      reason: resolution.reason,
    });
    writeCachedActiveTemplate(siteKey, companyId, domainType, publishedTemplate);
    activeTemplateResultsThisLoad.set(cacheKey, resolution);
    return resolution;
  })()
    .catch((error) => {
      setActiveTemplateApiState(companyId, domainType, {
        status: 'error',
        responseCount: null,
        hasPublishedTemplate: false
      });
      return {
        status: 'error',
        template: null,
        responseCount: null,
        reason: error?.message || 'template-request-failed',
        source: 'network',
        checkedAt: Date.now(),
      };
    })
    .finally(() => {
      activeTemplateRequests.delete(cacheKey);
    });

  activeTemplateRequests.set(cacheKey, request);
  return request;
};

export const fetchActiveTemplate = async (companyId, domainType, options = {}) => {
  const resolution = await resolveActiveTemplate(companyId, domainType, options);
  return resolution?.template || null;
};

export const findTemplates = async (criteria = {}) => {
  const response = await apiClient.post(templates.find, criteria);
  const payload = response?.data ?? response?.payload?.data ?? response;
  if (Array.isArray(payload)) return payload;
  const templatesList = collectTemplatePayloads(payload);
  if (templatesList.length) return templatesList;
  const template = extractTemplatePayload(payload);
  return template ? [template] : [];
};
